import React, { useState } from "react";
import { Button, message, Space, Checkbox, Modal, Dropdown, Grid, Tooltip } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  MoreOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import type { MenuProps } from "antd";
import { getFunctions, httpsCallable } from "firebase/functions";
import Master_Device_RegForm from "./Master_Device_RegForm";
import { Device } from "./web_parts/types";
import { useDevices } from "./web_parts/DeviceProvider";
import "../../styles/global.css";
import DeviceListModuleSingle from "./web_parts/DeviceList_module_single";

const { useBreakpoint } = Grid;

const Master_Device: React.FC = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const { refreshDevices } = useDevices();
  const [selectedRow, setSelectedRow] = useState<Device | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [deleteDialogDeviceId, setDeleteDialogDeviceId] = useState<string | null>(null);
  const [deleteAuthChecked, setDeleteAuthChecked] = useState(true);
  const [resetDialogDeviceId, setResetDialogDeviceId] = useState<string | null>(null);
  const [resetResultOpen, setResetResultOpen] = useState(false);
  const [resetResultText, setResetResultText] = useState("");

  const getRegionFunctions = () => getFunctions(undefined, "asia-northeast1");

  const handleNew = () => {
    setSelectedRow(null);
    setIsModalVisible(true);
  };

  const handleEdit = (record: Device) => {
    setSelectedRow(record);
    setIsModalVisible(true);
  };

  const handleDelete = async (record: Device, shouldDeleteAuthUser: boolean) => {
    try {
      const functions = getRegionFunctions();
      const deleteDeviceFn = httpsCallable<
        { deviceId: string; deleteAuthUser: boolean },
        { success: boolean; deletedAuthUser?: boolean }
      >(functions, "deleteDevice");
      const result = await deleteDeviceFn({
        deviceId: record.id,
        deleteAuthUser: shouldDeleteAuthUser,
      });

      const deletedAuthUser = (result.data as { deletedAuthUser?: boolean })?.deletedAuthUser === true;
      if (shouldDeleteAuthUser && deletedAuthUser) {
        message.success("デバイスとAuthenticationの匿名認証情報を削除しました");
      } else if (shouldDeleteAuthUser) {
        message.success("デバイスを削除しました（Authenticationの削除対象はありませんでした）");
      } else {
        message.success("デバイスを削除しました");
      }
      refreshDevices();
      if (selectedRow?.id === record.id) {
        setSelectedRow(null);
      }
    } catch (error) {
      // Callableの詳細メッセージを優先して表示
      const err = error as { message?: string };
      message.error(err?.message || "デバイスの削除に失敗しました");
    } finally {
      setDeleteDialogDeviceId(null);
      setDeleteAuthChecked(true);
    }
  };

  const handleFormClose = (isUpdated?: boolean) => {
    setIsModalVisible(false);
    if (isUpdated) {
      refreshDevices();
    }
  };

  const handleResetSetupData = async (record: Device) => {
    try {
      const functions = getRegionFunctions();
      const resetFn = httpsCallable<
        { deviceId: string },
        {
          success: boolean;
          deviceId: string;
          deviceLogicalDeleted: boolean;
          deviceArchived?: boolean;
          devicePhysicallyDeleted?: boolean;
          deviceBackupId?: string | null;
          deviceAuthDeleted: boolean;
          deviceAuthDeleteReason?: string | null;
          resetCommandSent: boolean;
          resetCommandError?: string | null;
          reportMailSent: boolean;
          reportMailError?: string | null;
          summary?: {
            deletedUserCount: number;
            skippedUserCount: number;
            failedUserCount: number;
          };
          contentDeletion?: {
            bbsPostsDeleted: number;
            bbsSettingsDeleted: number;
            bbsUsersDeleted: number;
            sensorInfoDeleted: number;
            chatTimeDeleted: number;
            linkItemsDeleted?: number;
            agoraItemsDeleted?: number;
            agoraMimamoriDeleted?: number;
            albumImportSessionsDeleted?: number;
            bbsImagesDeleted: number;
            emergencyVideosDeleted: number;
          };
          userResults?: Array<{
            email: string;
            status: "deleted" | "skipped_in_use" | "failed";
            authDeleted: boolean;
            loginUsersDeletedCount: number;
            reason?: string;
          }>;
          failures?: string[];
        }
      >(functions, "resetDeviceSetupData", { timeout: 300000 });
      const result = await resetFn({ deviceId: record.id });
      const data = result.data;
      const lines: string[] = [];
      lines.push(`デバイスID: ${data.deviceId}`);
      lines.push(
        `devices退避(devices_backup): ${data.deviceArchived ? "成功" : "失敗"}`
      );
      lines.push(`devices物理削除: ${data.devicePhysicallyDeleted ? "成功" : "失敗"}`);
      if (data.deviceBackupId) {
        lines.push(`バックアップID: ${data.deviceBackupId}`);
      }
      lines.push(
        `デバイスAuth削除: ${data.deviceAuthDeleted ? "成功" : `未削除 (${data.deviceAuthDeleteReason ?? "unknown"})`}`
      );
      lines.push(
        `タブレット初期化通知: ${data.resetCommandSent ? "送信成功" : `未送信 (${data.resetCommandError ?? "unknown"})`}`
      );
      lines.push(
        `結果メール送信(firebase@di-m.net): ${data.reportMailSent ? "成功" : `失敗 (${data.reportMailError ?? "unknown"})`}`
      );
      if (data.summary) {
        lines.push(
          `ユーザー結果: 削除成功 ${data.summary.deletedUserCount}件 / 対象外 ${data.summary.skippedUserCount}件 / 失敗 ${data.summary.failedUserCount}件`
        );
      }
      if (data.contentDeletion) {
        lines.push(
          `掲示板投稿: ${data.contentDeletion.bbsPostsDeleted}件 / 掲示板設定: ${data.contentDeletion.bbsSettingsDeleted}件 / 掲示板ユーザー: ${data.contentDeletion.bbsUsersDeleted}件`
        );
        lines.push(
          `みまもりセンサー: ${data.contentDeletion.sensorInfoDeleted}件 / 会話時間(chat_time): ${data.contentDeletion.chatTimeDeleted ?? 0}件`
        );
        lines.push(
          `リンク集(linkitems): ${data.contentDeletion.linkItemsDeleted ?? 0}件 / 会議(agoraitems): ${data.contentDeletion.agoraItemsDeleted ?? 0}件 / 緊急みまもり: ${data.contentDeletion.agoraMimamoriDeleted ?? 0}件`
        );
        lines.push(
          `アルバムQRセッション: ${data.contentDeletion.albumImportSessionsDeleted ?? 0}件 / BBS画像: ${data.contentDeletion.bbsImagesDeleted}件 / 緊急動画: ${data.contentDeletion.emergencyVideosDeleted}件`
        );
      }
      if (data.userResults && data.userResults.length > 0) {
        lines.push("");
        lines.push("【ユーザー別結果】");
        data.userResults.forEach((u) => {
          const statusLabel =
            u.status === "deleted" ? "削除成功" : u.status === "skipped_in_use" ? "削除対象外(他デバイス参照)" : "削除失敗";
          lines.push(
            `${u.email} / ${statusLabel} / loginusers削除=${u.loginUsersDeletedCount} / auth削除=${u.authDeleted ? "有" : "無"}${
              u.reason ? ` / 理由=${u.reason}` : ""
            }`
          );
        });
      }
      if (data.failures && data.failures.length > 0) {
        lines.push("");
        lines.push("【処理失敗詳細】");
        data.failures.forEach((f) => lines.push(`- ${f}`));
      }
      setResetResultText(lines.join("\n"));
      setResetResultOpen(true);
      message.success("セットアップデータ全削除の処理が完了しました");
      refreshDevices();
      if (selectedRow?.id === record.id) {
        setSelectedRow(null);
      }
    } catch (error) {
      const err = error as { message?: string };
      message.error(err?.message || "セットアップデータ全削除に失敗しました");
    } finally {
      setResetDialogDeviceId(null);
    }
  };
  
  // --- columns定義は不要なため削除 ---

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: "8px" }}>
        <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
          <Button type="primary" onClick={handleNew}>新規登録</Button>
          <Button icon={<ReloadOutlined />} onClick={refreshDevices}>更新</Button>
        </div>
      </div>

      <DeviceListModuleSingle
        variant="full"
        onDeviceSelected={setSelectedRow}
        selectedDeviceId={selectedRow?.id}
        renderActions={(record) => {
          const stop = (e: React.MouseEvent) => e.stopPropagation();

          if (isMobile) {
            const items: MenuProps["items"] = [
              { key: "edit", label: "編集", icon: <EditOutlined /> },
              { key: "delete", label: "削除", icon: <DeleteOutlined />, danger: true },
              { key: "reset", label: "セットアップ全削除", icon: <ClearOutlined />, danger: true },
            ];
            return (
              <Dropdown
                menu={{
                  items,
                  onClick: ({ key, domEvent }) => {
                    domEvent.stopPropagation();
                    if (key === "edit") handleEdit(record);
                    if (key === "delete") {
                      setDeleteDialogDeviceId(record.id);
                      setDeleteAuthChecked(true);
                    }
                    if (key === "reset") setResetDialogDeviceId(record.id);
                  },
                }}
                trigger={["click"]}
              >
                <Button icon={<MoreOutlined />} onClick={stop} />
              </Dropdown>
            );
          }

          return (
            <Space size={4} onClick={stop}>
              <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() => {
                  setDeleteDialogDeviceId(record.id);
                  setDeleteAuthChecked(true);
                }}
              />
              <Tooltip title="セットアップ全削除">
                <Button
                  danger
                  icon={<ClearOutlined />}
                  onClick={() => setResetDialogDeviceId(record.id)}
                />
              </Tooltip>
            </Space>
          );
        }}
      />

      <Modal
        title="削除確認"
        open={!!deleteDialogDeviceId}
        onOk={() => {
          if (!deleteDialogDeviceId) return;
          void handleDelete({ id: deleteDialogDeviceId } as Device, deleteAuthChecked);
        }}
        onCancel={() => {
          setDeleteDialogDeviceId(null);
          setDeleteAuthChecked(true);
        }}
        okText="はい"
        cancelText="いいえ"
        okButtonProps={{ danger: true }}
        destroyOnClose
      >
        <div style={{ marginBottom: 8 }}>このデバイスを削除しますか？（一覧から非表示になります）</div>
        <Checkbox
          checked={deleteAuthChecked}
          onChange={(e) => setDeleteAuthChecked(e.target.checked)}
        >
          Authenticationの匿名認証情報も削除する
        </Checkbox>
      </Modal>

      <Modal
        title="セットアップデータ全削除"
        open={!!resetDialogDeviceId}
        onOk={() => {
          if (!resetDialogDeviceId) return;
          void handleResetSetupData({ id: resetDialogDeviceId } as Device);
        }}
        onCancel={() => setResetDialogDeviceId(null)}
        okText="実行"
        cancelText="キャンセル"
        okButtonProps={{ danger: true }}
        destroyOnClose
      >
        このデバイスのセットアップデータを全削除します。掲示板・センサー・会話時間・リンク集・会議データ・クラウド上の掲示板画像／緊急動画なども削除されます。端末内のアルバム写真は残ります。処理は続行され、結果はダイアログとメールで通知されます。実行しますか？
      </Modal>

      <Master_Device_RegForm
        visible={isModalVisible}
        onClose={handleFormClose}
        deviceData={selectedRow}
      />
      <Modal
        title="セットアップデータ全削除 結果"
        open={resetResultOpen}
        onOk={() => setResetResultOpen(false)}
        onCancel={() => setResetResultOpen(false)}
        okText="OK"
        cancelButtonProps={{ style: { display: "none" } }}
      >
        <pre
          style={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            background: "#fafafa",
            border: "1px solid #f0f0f0",
            borderRadius: 8,
            padding: 12,
            maxHeight: 420,
            overflow: "auto",
            margin: 0,
          }}
        >
          {resetResultText}
        </pre>
      </Modal>
    </>
  );
};

export default Master_Device;
